import { useMemo, useCallback, useState, forwardRef } from "react";
import { SelectArrayInput, useChoices, useInput, FieldTitle, useResourceContext } from "react-admin";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { Row } from "../misc/Row";
import { Box, IconButton, OutlinedInput, InputAdornment, Divider, Paper, type PaperProps } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import { useThemeColor } from "../hooks/useThemeColor";
import { FilterModeDropdownMenu } from "./FilterModeDropdownMenu";

export function MultiSelectInput(props: {
  source: string;
  choices: Array<{ id: string | number; name: string }>;
  label: string;
  emptyText?: string;
  alwaysOn?: boolean;
  variant?: "text" | "outlined";
  withMode?: boolean;
  withSearch?: boolean;
}) {
  const { source, choices, variant = "outlined", withMode, withSearch, label, emptyText, ...rest } = props;
  const { field } = useInput({ source });
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const normalizedSearchTerm = searchTerm.trim().toLocaleLowerCase("de-DE");
  const filteredChoices = withSearch
    ? choices.filter((choice) => choice.name.toLocaleLowerCase("de-DE").includes(normalizedSearchTerm))
    : choices;
  const primaryColor = useThemeColor("primary");
  const selectedValues = useMemo(
    () => (Array.isArray(field.value) ? field.value : field.value ? [field.value] : []),
    [field.value],
  );
  const { getChoiceText, getChoiceValue } = useChoices({
    optionText: "name",
    optionValue: "id",
  });
  const resource = useResourceContext();
  const hasValue = selectedValues.length > 0;
  const handleClear = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      field.onChange([]);
    },
    [field],
  );
  const hoverStyles =
    variant === "text" && !hasValue
      ? {
          backgroundColor: "action.hover",
          "& .MuiSvgIcon-root": { color: "text.secondary" },
          "& .MuiSelect-select": {
            color: "text.secondary",
            backgroundColor: "transparent !important",
            fontWeight: 500,
          },
        }
      : undefined;
  const ModeTogglePaper = useMemo(
    () =>
      forwardRef<HTMLDivElement, PaperProps>(function ModeTogglePaper(paperProps, ref) {
        return (
          <Paper ref={ref} {...paperProps}>
            <Box sx={{ px: 1.5, pt: 1 }}>
              <FilterModeDropdownMenu source={source} />
            </Box>
            {paperProps.children}
          </Paper>
        );
      }),
    [source],
  );
  const SearchPaper = useMemo(
    () =>
      forwardRef<HTMLDivElement, PaperProps>(function SearchPaper(paperProps, ref) {
        return (
          <Paper ref={ref} {...paperProps}>
            <Box sx={{ px: 1.5, pt: 1 }}>
              <OutlinedInput
                autoFocus
                fullWidth
                size="small"
                placeholder="Suche"
                inputProps={{ "aria-label": `${label} suchen` }}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                }
                onChange={(event) => setSearchTerm(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key !== "Escape") {
                    event.stopPropagation();
                  }
                  if (event.key === "Enter") {
                    event.preventDefault();
                  }
                }}
              />
            </Box>
            <Divider sx={{ mb: 0.5, mt: 1.5 }} />
            {paperProps.children}
          </Paper>
        );
      }),
    [label],
  );

  const customInput = useMemo(
    () => (
      <OutlinedInput
        id={`select-multiple-${source}`}
        label={
          emptyText ? undefined : <FieldTitle label={label} source={source} resource={resource} isRequired={false} />
        }
        endAdornment={
          hasValue ? (
            <InputAdornment position="start">
              <IconButton
                size="small"
                onClick={handleClear}
                sx={{ mr: 0.5, "&:hover": { backgroundColor: primaryColor.light } }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined
        }
      />
    ),
    [source, label, resource, emptyText, hasValue, handleClear],
  );

  const input = (
    <SelectArrayInput
      source={source}
      label={label}
      className="fix-SelectArrayInput-label"
      choices={filteredChoices}
      helperText={false}
      optionText={(choice) => {
        const choiceValue = getChoiceValue(choice);
        const isSelected = selectedValues.some((val) => String(val) === String(choiceValue));
        return (
          <Row alignItems="center" gap={1}>
            {isSelected ? (
              <CheckBoxIcon sx={{ fontSize: 20, color: primaryColor.main }} />
            ) : (
              <CheckBoxOutlineBlankIcon sx={{ fontSize: 20 }} />
            )}
            <span>{getChoiceText(choice)}</span>
          </Row>
        );
      }}
      options={{
        renderValue: (selected) => {
          const selectedArray = Array.isArray(selected) ? selected : [];
          const selectedChoices = selectedArray
            .map((value) => choices.find((choice) => String(getChoiceValue(choice)) === String(value)))
            .filter((choice) => !!choice);
          if (selectedChoices.length >= 1 && selectedChoices[0]) {
            if (selectedChoices.length === 1) {
              return selectedChoices[0].name;
            }
            return `${selectedChoices[0].name} + ${selectedChoices.length - 1}`;
          }
          if (variant === "text") {
            return <FieldTitle label={label} source={source} resource={resource} isRequired={false} />;
          }
          if (emptyText) {
            return emptyText;
          }
          return undefined;
        },
        input: customInput,
        onOpen: () => {
          setSearchTerm("");
          setIsOpen(true);
        },
        onClose: () => {
          setSearchTerm("");
          setIsOpen(false);
        },
        IconComponent: isOpen ? ExpandLessIcon : ExpandMoreIcon,
        MenuProps: {
          ...(withSearch ? { autoFocus: false, disableAutoFocusItem: true } : undefined),
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "left",
          },
          PaperProps: {
            sx: {
              mt: "10px",
              boxShadow: 3,
              overflow: "hidden",
              ...(withSearch ? { width: "320px", maxWidth: "calc(100vw - 32px)" } : undefined),
              "& .MuiMenuItem-root": {
                fontSize: "14px",
              },
            },
            ...(withSearch ? { component: SearchPaper } : withMode ? { component: ModeTogglePaper } : undefined),
          },
          MenuListProps: {
            sx: {
              maxHeight: "min(320px, calc(100vh - 180px))",
              overflowY: "auto",
            },
          },
        },
        displayEmpty: variant === "text" || !!emptyText,
      }}
      sx={{
        ...(emptyText
          ? {
              "& > .MuiInputLabel-root": {
                position: "static",
                transform: "none",
                fontSize: "14px !important",
                fontWeight: 600,
                color: "text.primary",
                mb: 0.8,
              },
              "& > .MuiInputLabel-root.Mui-focused, & > .MuiInputLabel-root.MuiFormLabel-filled": {
                transform: "none",
              },
            }
          : undefined),
        "& .MuiOutlinedInput-root": {
          ...(hasValue ? { backgroundColor: primaryColor.main } : undefined),
          "& .MuiSvgIcon-root": { color: hasValue ? primaryColor.contrastText : "text.secondary" },
          "& .MuiSelect-select": {
            paddingRight: variant === "text" && !hasValue ? "32px" : "4px",
            color: hasValue ? primaryColor.contrastText : "text.secondary",
            fontWeight: 500,
            ...(hasValue ? { backgroundColor: "transparent !important" } : undefined),
            ...(variant === "text" ? { width: "fit-content", minWidth: "auto", maxWidth: "none" } : undefined),
          },
          "& .MuiSelect-icon": {
            ...(variant === "text" ? { right: "8px !important" } : undefined),
          },
          ...(variant === "text" ? { width: "fit-content", minWidth: "auto" } : undefined),
          ...(hoverStyles ? { "&:hover": hoverStyles } : undefined),
          ...(isOpen && hoverStyles
            ? {
                ...hoverStyles,
                ...(variant === "text" ? { width: "fit-content", minWidth: "auto" } : undefined),
              }
            : undefined),
        },
        ...(variant === "text" && hoverStyles
          ? {
              "&:hover .MuiOutlinedInput-root": hoverStyles,
              "& .MuiSelect-root:hover .MuiOutlinedInput-root": hoverStyles,
            }
          : undefined),
        "& .MuiInputLabel-root": {
          ...(variant === "text" || (hasValue && !emptyText) ? { display: "none" } : undefined),
        },
        ...(variant === "text" || hasValue
          ? {
              "& .MuiOutlinedInput-notchedOutline": { border: "none" },
              "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
            }
          : undefined),
      }}
      {...rest}
    />
  );

  return input;
}
